import React, { useState, useEffect, useMemo, useRef } from "react";
import { CssVarsProvider } from "@mui/joy/styles";
import CssBaseline from "@mui/joy/CssBaseline";
import Box from "@mui/joy/Box";
import Tabs from "devextreme-react/tabs";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";

import { useLocation, useNavigate, useOutlet } from "react-router-dom";
import { admin, vendor, notice, management } from "../menuItems";
import axios from "../api/axios";
import moment from "moment";
import "../styles/custom_tabs.css";

export default function RootLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const outlet = useOutlet(); // 현재 URL에 매칭되는 자식 컴포넌트

    // sessionStorage를 이용한 탭 목록 복구
    const [tabs, setTabs] = useState(() => {
        try {
            const savedTabs = sessionStorage.getItem("openedTabs");
            return savedTabs ? JSON.parse(savedTabs) : [];
        } catch (e) {
            console.error("탭 복구 실패", e);
            return [];
        }
    });

    const [latestNotice, setLatestNotice] = useState(null);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    // 렌더링 시점에 화면 컴포넌트를 저장할 객체
    const cachedOutlets = useRef({});
    const closedTabPathRef = useRef("");

    const menuItems = useMemo(() => [notice, ...admin.children, ...vendor.children, ...management.children], []);

    // 탭 목록이 변경될 때마다 sessionStorage 업데이트
    useEffect(() => {
        sessionStorage.setItem("openedTabs", JSON.stringify(tabs));
    }, [tabs]);

    useEffect(() => {
        if (location.pathname === "/" || location.pathname === "/login") {
            setTabs([]);
            setSelectedTabIndex(0);
            cachedOutlets.current = {};
            return;
        }

        if (closedTabPathRef.current === location.pathname) {
            closedTabPathRef.current = "";
            return;
        }
        if (closedTabPathRef.current && closedTabPathRef.current !== location.pathname) {
            closedTabPathRef.current = "";
        }

        const currentMenuItem = menuItems.find((item) => item.url === location.pathname);
        if (!currentMenuItem) return;

        setTabs((prevTabs) => {
            const existingTabIndex = prevTabs.findIndex((tab) => tab.path === location.pathname);
            let newTabs = [...prevTabs];

            if (existingTabIndex === -1) {
                // 1. 신규 탭 오픈
                newTabs.push({
                    id: location.pathname,
                    key: location.pathname,
                    path: location.pathname,
                    text: currentMenuItem.title,
                });
                setSelectedTabIndex(newTabs.length - 1);
            } else {
                // 2. 기존 탭으로 이동
                // [문제 해결 핵심] 복잡했던 '강제 초기화(renderKey 변경)' 로직을 완전히 제거했습니다.
                // 이제 이미 열려있는 탭으로 진입하면, 어떤 경우에도 컴포넌트를 파기하지 않고 상태를 무조건 유지합니다.
                setSelectedTabIndex(existingTabIndex);
            }
            return newTabs;
        });

        // 브라우저 타이틀 변경 로직
        const getDocumentTitle = () => {
            if (process.env.REACT_APP_API_URL?.includes("localhost")) return "DSNL QMS TEST";
            else if (process.env.REACT_APP_API_URL?.includes("api.dsnl.co.kr")) return "DSNL QMS";
            else return "DSNL QMS DEV";
        };
        document.title = getDocumentTitle();
    }, [location.pathname, menuItems]);

    useEffect(() => {
        const fetchLatestNotice = async () => {
            try {
                const response = await axios.get("/CmNotice?$orderby=InsertDate desc&$top=1");
                if (response.data.value && response.data.value.length > 0) {
                    setLatestNotice(response.data.value[0]);
                }
            } catch (error) {
                console.error("최신 공지사항을 가져오는 데 실패했습니다:", error);
            }
        };
        fetchLatestNotice();
    }, []);

    // [이벤트 보완] 사용자가 마우스로 탭을 클릭하는 즉시 물리적 이벤트를 감지하여 안전하게 이동시킵니다.
    const handleTabClick = (e) => {
        if (closedTabPathRef.current) return;
        const clickedTab = e.itemData;
        if (clickedTab && clickedTab.path !== location.pathname) {
            navigate(clickedTab.path);
        }
    };

    const handleTabSelection = (e) => {
        if (e.name === "selectedIndex" && e.value !== selectedTabIndex) {
            if (closedTabPathRef.current) return;
            const newPath = tabs[e.value]?.path;
            if (newPath && newPath !== location.pathname) {
                navigate(newPath);
            }
        }
    };

    const onTabClose = (item, e) => {
        e.stopPropagation();

        const itemIndex = tabs.findIndex((tab) => tab.key === item.key);
        const newTabs = tabs.filter((tab) => tab.key !== item.key);

        // 탭이 명시적으로 닫힐 때만 메모리 최적화를 위해 캐시 데이터 삭제
        delete cachedOutlets.current[item.path];
        setTabs(newTabs);

        if (newTabs.length === 0) {
            closedTabPathRef.current = item.path;
            navigate("/");
        } else {
            if (itemIndex === selectedTabIndex) {
                closedTabPathRef.current = item.path;
                const nextTabIndex = Math.max(0, itemIndex - 1);
                navigate(newTabs[nextTabIndex].path);
            }
        }
    };

    const renderTab = (item) => {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span>{item.text}</span>
                <i className="dx-icon dx-icon-close" style={{ cursor: "pointer", fontSize: "14px" }} onClick={(e) => onTabClose(item, e)} />
            </div>
        );
    };

    return (
        <CssVarsProvider disableTransitionOnChange>
            <CssBaseline />
            <Box sx={{ display: "flex", minHeight: "100dvh" }}>
                <Header />
                <Sidebar />
                <Box
                    component="main"
                    sx={{
                        px: { xs: 1, md: 1 },
                        pt: { xs: "calc(12px + var(--Header-height))", sm: "calc(12px + var(--Header-height))", md: 1 },
                        pb: { xs: 1, sm: 1, md: 1 },
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                        height: "100dvh",
                        backgroundColor: "#FAFAFB",
                    }}
                >
                    {tabs.length > 0 && (
                        <Tabs
                            dataSource={tabs}
                            selectedIndex={selectedTabIndex}
                            onItemClick={handleTabClick} // 물리적인 클릭 감지 로직 연결
                            onOptionChanged={handleTabSelection}
                            itemRender={renderTab}
                            showNavButtons={true}
                            scrollByContent={true}
                            scrollingEnabled={true}
                            width={"auto"}
                            className="custom-tabs"
                        />
                    )}
                    <Box sx={{ flex: 1, overflow: "auto", pt: 0.5 }}>
                        {tabs.map((tab) => {
                            const isActive = location.pathname === tab.path;

                            if (isActive) {
                                cachedOutlets.current[tab.path] = outlet;
                            }

                            return (
                                <div
                                    key={tab.key} // renderKey 대신 영구적인 탭 키를 사용하여 컴포넌트 재생성(초기화) 방지
                                    style={{ display: isActive ? "block" : "none", height: "100%" }}
                                >
                                    {cachedOutlets.current[tab.path]}
                                </div>
                            );
                        })}
                    </Box>

                    {/*공지 사항*/}
                    <Box
                        onClick={() => navigate("/notice")}
                        sx={{
                            height: "40px",
                            mt: 1,
                            mb: 0.5,
                            backgroundColor: "background.surface",
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            cursor: "pointer",
                            borderRadius: "sm",
                            boxShadow: "sm",
                            transition: "box-shadow 0.2s",
                            "&:hover": { boxShadow: "md" },
                        }}
                    >
                        <span style={{ fontWeight: "bold", color: "#d9534f", width: "50px", marginRight: "10px" }}>[공지]</span>
                        {latestNotice ? (
                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "1000px", alignItems: "center" }}>
                                <Box sx={{ width: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "14px" }}>{latestNotice.Title}</Box>
                                <Box sx={{ width: "200px", textAlign: "left", fontSize: "14px" }}>{moment(latestNotice.InsertDate).format("YYYY-MM-DD HH:mm")}</Box>
                                <Box />
                            </Box>
                        ) : (
                            <span>최신 공지사항을 불러오는 중입니다...</span>
                        )}
                    </Box>
                </Box>
            </Box>
        </CssVarsProvider>
    );
}
